import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, BarChart3, Megaphone } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

const COOKIE_CONSENT_KEY = 'onsitehand_cookie_consent';

export function getCookieConsent() {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading cookie consent:', e);
  }
  return null;
}

export function setCookieConsent(consent) {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      ...consent,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }));
  } catch (e) {
    console.error('Error saving cookie consent:', e);
  }
}

export default function CookiePreferences({ open, onClose }) {
  const { get } = useSiteContent();
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const stored = getCookieConsent();
    if (stored) {
      setPreferences({
        essential: true,
        analytics: stored.analytics ?? false,
        marketing: stored.marketing ?? false
      });
    }
  }, [open]);

  const handleSave = () => {
    setCookieConsent(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    setCookieConsent(allAccepted);
    onClose();
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = { essential: true, analytics: false, marketing: false };
    setPreferences(essentialOnly);
    setCookieConsent(essentialOnly);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Cookie className="h-5 w-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-slate-900">{get('cookie.preferences.title', 'Cookie Preferences')}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1"
            data-testid="close-cookie-prefs"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <p className="text-sm text-slate-600">
            We use cookies to improve your experience on OnsiteHand. You can choose which types of cookies to allow.
          </p>

          {/* Essential Cookies */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-slate-900">{get('cookie.preferences.essential_title', 'Essential Cookies')}</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {get('cookie.preferences.essential_desc', 'Required for the website to function. Cannot be disabled.')}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    Includes: Authentication, security, session management
                  </p>
                </div>
              </div>
              <div className="shrink-0">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                  Always On
                </span>
              </div>
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <BarChart3 className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-slate-900">{get('cookie.preferences.analytics_title', 'Analytics Cookies')}</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {get('cookie.preferences.analytics_desc', 'Help us understand how visitors use our website.')}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    Includes: Page views, feature usage, performance data
                  </p>
                </div>
              </div>
              <div className="shrink-0">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Marketing Cookies */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Megaphone className="h-5 w-5 text-purple-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-slate-900">{get('cookie.preferences.marketing_title', 'Marketing Cookies')}</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {get('cookie.preferences.marketing_desc', 'Used to deliver relevant advertisements.')}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    Includes: Advertising, social media, personalisation
                  </p>
                </div>
              </div>
              <div className="shrink-0">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-slate-200 space-y-2">
          <div className="flex gap-2">
            <button
              onClick={handleRejectNonEssential}
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Essential Only
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Accept All
            </button>
          </div>
          <button
            onClick={handleSave}
            className="w-full px-4 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            {get('cookie.preferences.save_btn', 'Save Preferences')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Cookie Banner Component (shown on first visit)
export function CookieBanner() {
  const { get } = useSiteContent();
  const [show, setShow] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setShow(true);
    }
  }, []);

  const handleAcceptAll = () => {
    setCookieConsent({ essential: true, analytics: true, marketing: true });
    setShow(false);
  };

  const handleRejectNonEssential = () => {
    setCookieConsent({ essential: true, analytics: false, marketing: false });
    setShow(false);
  };

  if (!show) return <CookiePreferences open={showPrefs} onClose={() => setShowPrefs(false)} />;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Cookie className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600">
              {get('cookie.banner.text', 'We use cookies to improve your experience. By continuing, you agree to our')}{' '}
              <a href="/cookies" className="text-orange-600 hover:underline">{get('cookie.banner.policy_link', 'Cookie Policy')}</a>.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowPrefs(true)}
              className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800"
            >
              {get('cookie.banner.manage_btn', 'Manage')}
            </button>
            <button
              onClick={handleRejectNonEssential}
              className="px-3 py-1.5 text-sm text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
            >
              {get('cookie.banner.reject_btn', 'Reject')}
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-4 py-1.5 text-sm text-white bg-orange-500 rounded-lg hover:bg-orange-600"
            >
              {get('cookie.banner.accept_btn', 'Accept')}
            </button>
          </div>
        </div>
      </div>
      <CookiePreferences open={showPrefs} onClose={() => setShowPrefs(false)} />
    </>
  );
}
